import * as React from 'react';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Translate } from '../../../../../shared/LanguageService';
import { BookingDateSelect } from '../../../../photographerBooking/se360/v2/shared/bookingDateSelect/view/BookingDateSelect';
import { AvailablePhotographer } from '../../../../photographerBooking/se360/v2/shared/types';
import { BookingChoice } from '../types';

type PhotographerChoiceListProps = {
    photographers: AvailablePhotographer[];
    bookingDate: Date;
    choice: BookingChoice | null;
    isLoading: boolean;
    onChangeDate: (date: Date) => void;
    onChoose: (choice: BookingChoice) => void;
    earliestBookableDate?: Date | null;
};

export function PhotographerChoiceList({
    photographers,
    bookingDate,
    choice,
    isLoading,
    onChangeDate,
    onChoose,
    earliestBookableDate = null
}: PhotographerChoiceListProps) {
    return (
        <Box sx={{ display: 'grid', gap: '16px' }}>
            <BookingDateSelect
                showHint={false}
                bookingDate={bookingDate}
                disabled={false}
                onChange={onChangeDate}
                earliestBookableDate={earliestBookableDate}
            />
            {isLoading &&
                [0, 1, 2].map((card) => (
                    <Skeleton key={card} variant="rectangular" height={64} aria-busy="true" />
                ))}
            {!isLoading && photographers.length === 0 && (
                <Typography>
                    {Translate('order/create/v2/step/photographer/no-photographers')}
                </Typography>
            )}
            {!isLoading &&
                photographers.map((photographer) => {
                    let headingId = `create-order-photographer-${photographer.id}`;
                    let isChosen = choice !== null && choice.photographerId === photographer.id;

                    return (
                        <Paper
                            key={photographer.id}
                            component="section"
                            variant="outlined"
                            aria-labelledby={headingId}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '12px'
                            }}
                        >
                            <Avatar
                                alt=""
                                src={
                                    photographer.profileImageReference
                                        ? `${window.appConfig.apiUrl}/file/${photographer.profileImageReference}`
                                        : undefined
                                }
                            />
                            <Typography id={headingId} sx={{ flex: 1, fontSize: '14px' }}>
                                {photographer.name}
                            </Typography>
                            <TextField
                                select
                                size="small"
                                sx={{ width: '160px' }}
                                SelectProps={{ native: true }}
                                InputLabelProps={{ shrink: true }}
                                label={Translate('order/create/v2/step/photographer/choose-time')}
                                value={isChosen ? choice.timeslot : ''}
                                onChange={(event) =>
                                    onChoose({
                                        photographerId: photographer.id,
                                        timeslot: event.target.value
                                    })
                                }
                            >
                                <option value="" disabled />
                                {photographer.availableTimeslots.map((timeslot) => (
                                    <option key={timeslot.time} value={timeslot.time}>
                                        {timeslot.time}
                                    </option>
                                ))}
                            </TextField>
                        </Paper>
                    );
                })}
        </Box>
    );
}
